import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import HorizontalRule from '@components/HorizontalRule';
import SummaryRow from '@components/SummaryRow';

import type { CostCalculatorCosts } from '@hooks/useCostCalculator';

import { formatMoney } from '@utils/format';

import {
  TotalsLeft,
  TotalsLine,
  TotalsRight,
} from './PaymentSummarySection.style';

const messages = defineMessages({
  dueMonthly: {
    id: 'summaryView.dueMonthly',
    defaultMessage: '/month',
  },
  dueAnnually: {
    id: 'summaryView.dueAnnually',
    defaultMessage: '/year',
  },
  perMonth: {
    id: 'summaryView.perMonth',
    defaultMessage: '/mo',
  },
  perYear: {
    id: 'summaryView.perYear',
    defaultMessage: '/yr',
  },
  personalAccount: {
    id: 'summaryView.personalAccount',
    defaultMessage: 'Patient Account',
  },
  additionalAccount: {
    id: 'summaryView.additionalAccount',
    defaultMessage: 'Additional Patient Account(s)',
  },
  recordCollection: {
    id: 'summaryView.recordCollection',
    defaultMessage: 'Setup Fees (one-time)',
  },
  free: {
    id: 'summaryView.free',
    defaultMessage: 'Free',
  },
  caregiverAct: {
    id: 'summaryView.caregiverAct',
    defaultMessage: 'User Account(s)',
  },
  promoCode: {
    id: 'summaryView.promoCode',
    defaultMessage: 'Promo Code',
  },
  subtotal: {
    id: 'summaryView.subtotal',
    defaultMessage: 'Subtotal',
  },
  taxes: {
    id: 'summaryView.taxes',
    defaultMessage: 'Taxes',
  },
  total: {
    id: 'summaryView.total',
    defaultMessage: 'Total',
  },
});

export default function PaymentSummarySection({
  costs,
}: {
  costs: CostCalculatorCosts;
}) {
  const { formatMessage } = useIntl();

  const {
    plans,
    numPlans,
    subscriptionCosts,
    oneTimeCost,
    promo,
    subtotal,
    taxes,
    total,
    loading,
    currency,
    coupon,
    taxRate,
    billingPeriod,
  } = costs;

  const isMonthly = billingPeriod === 'month';

  if (loading) {
    return null;
  }

  return (
    <>
      {numPlans.PATIENT > 0 && (
        <>
          <SummaryRow
            bottomText={`${numPlans.PATIENT} x ${formatMoney(
              plans.PATIENT?.getSubscriptionCost(billingPeriod, currency)
            )} ${
              isMonthly
                ? formatMessage(messages.dueMonthly)
                : formatMessage(messages.dueAnnually)
            }`}
            rightText={`${formatMoney(subscriptionCosts.PATIENT)}${
              isMonthly
                ? formatMessage(messages.perMonth)
                : formatMessage(messages.perYear)
            }`}
            topText={formatMessage(messages.personalAccount)}
          />
          <HorizontalRule />
        </>
      )}

      {numPlans.PATIENT > 0 && oneTimeCost ? (
        <>
          <SummaryRow
            bottomText={`${numPlans.PATIENT} x ${formatMoney(
              plans.PATIENT?.getOneTimeCost(billingPeriod, currency)
            )}`}
            rightText={formatMoney(oneTimeCost)}
            topText={formatMessage(messages.recordCollection)}
          />
          <HorizontalRule />
        </>
      ) : null}

      {coupon?.id ? (
        <>
          <SummaryRow
            bottomText={coupon.name}
            rightText={formatMoney(promo)}
            topText={`${formatMessage(messages.promoCode)} (${coupon.id})`}
          />
          <HorizontalRule />
        </>
      ) : null}

      <TotalsLine>
        <TotalsLeft>{formatMessage(messages.subtotal)}</TotalsLeft>
        <TotalsRight>{formatMoney(subtotal)}</TotalsRight>
      </TotalsLine>

      <TotalsLine>
        <TotalsLeft>
          {`${formatMessage(messages.taxes)} ${taxRate ? taxRate * 100 : 0}%`}
        </TotalsLeft>
        <TotalsRight>{formatMoney(taxes)}</TotalsRight>
      </TotalsLine>

      <TotalsLine>
        <TotalsLeft bold>{formatMessage(messages.total)}</TotalsLeft>
        <TotalsRight bold>{formatMoney(total)}</TotalsRight>
      </TotalsLine>
    </>
  );
}
